import { ContentNuggetBookmark, ContentNuggetBookmarkTypes } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';

export const bookmarksApi = createApi({
  reducerPath: 'bookmarksApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Bookmarks'],
  endpoints: (builder) => ({
    /**
     * Fetch all bookmarked content nuggets
     */
    getBookmarks: builder.query<ContentNuggetBookmark[], undefined>({
      query: () => 'content-nugget-bookmark',
      keepUnusedDataFor: 60 * 60 * 24, // Cache for 24 hours
      providesTags: [{ type: 'Bookmarks' }],
    }),

    /**
     * Post a content nugget as bookmarked
     */
    postBookmark: builder.mutation<
      ContentNuggetBookmark[],
      {
        itemId: number | string;
        type: ContentNuggetBookmarkTypes;
      }
    >({
      query: ({ itemId, type }) => ({
        url: 'content-nugget-bookmark',
        method: 'POST',
        body: {
          ...(type === 'nugget' ? { nugget: { id: itemId } } : undefined),
          ...(type === 'scholarship' ? { scholarship: { id: itemId } } : undefined),
          ...(type === 'career-path' ? { career_path: { id: itemId } } : undefined),
          ...(type === 'career-track' ? { career_track: { id: itemId } } : undefined),
          ...(type === 'profile' ? { profile: { id: itemId } } : undefined),
          ...(type === 'external-profile' ? { external_profile: { id: itemId } } : undefined),
          ...(type === 'internship' ? { internship: { id: itemId } } : undefined),
          ...(type === 'post' ? { post: { id: itemId } } : undefined),
          ...(type === 'college' ? { college: { id: itemId } } : undefined),
          ...(type === 'generated-content' ? { generated_content: { id: itemId } } : undefined),
          ...(type === 'track-assessment-result'
            ? { track_assessment_result: { id: itemId } }
            : undefined),
          ...(type === 'track-assessment-unlock'
            ? { track_assessment_unlock: { id: itemId } }
            : undefined),
          ...(type === 'educational-investment'
            ? { educational_investment: { id: itemId } }
            : undefined),
          ...(type === 'tool' ? { tool: { id: itemId } } : undefined),
          ...(type === 'employer' ? { employer: { id: itemId } } : undefined),
          ...(type === 'competency' ? { competency: { id: itemId } } : undefined),
          ...(type === 'major' ? { major: { id: itemId } } : undefined),
          ...(type === 'industry' ? { industry: { id: itemId } } : undefined),
          ...(type === 'coach' ? { coach: { id: itemId } } : undefined),
          ...(type === 'deep-dive' ? { deep_dive: { id: itemId } } : undefined),
          ...(type === 'blueprint' ? { blueprint: { id: itemId } } : undefined),
        },
      }),
      invalidatesTags: [{ type: 'Bookmarks' }],
    }),

    /**
     * Delete a content nugget bookmark
     */
    deleteBookmark: builder.mutation<ContentNuggetBookmark[], number>({
      query: (bookmarkId) => ({
        url: `content-nugget-bookmark/${bookmarkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Bookmarks' }],
    }),
  }),
});

export const {
  useGetBookmarksQuery,
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  util: { resetApiState },
} = bookmarksApi;

// export mutation function types
export type PostBookmarkMutationFn = ReturnType<typeof usePostBookmarkMutation>[0];
export type DeleteBookmarkMutationFn = ReturnType<typeof useDeleteBookmarkMutation>[0];
